import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import axios from 'axios';
import gql from 'graphql-tag';
import { openIde } from '../../../util/code-editor';

const GET_SITE_LOGO_IMAGE = gql`
  query {
    site {
      headerHtml
      legacyLogoImage {
        id
        url
      }
      headerOverrideHtml
      footerOverrideHtml
    }
  }
`;

interface SiteData {
  site: {
    headerHtml: string;
    legacyLogoImage: {
      id: string;
      url: string;
    };
    headerOverrideHtml: string | null;
    footerOverrideHtml: string | null;
  };
}

const UPDATE_SITE = gql`
  mutation UpdateSiteLogoImage($input: UpdateSiteInput!) {
    updateSite(input: $input) {
      site {
        id
      }
    }
  }
`;

export const useAdminSiteSettingsPanelController = () => {
  const [logoImageFile, setLogoImageFile] = useState<File | undefined>(
    undefined,
  );

  const [logoImageId, setLogoImageId] = useState<string | undefined>(undefined);

  const [logoImageUrl, setLogoImageUrl] = useState<string | undefined>(
    undefined,
  );

  const { data, loading, refetch } = useQuery<SiteData>(GET_SITE_LOGO_IMAGE, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (data && data.site && data.site.legacyLogoImage) {
      setLogoImageUrl(data.site.legacyLogoImage.url);
      setLogoImageId(data.site.legacyLogoImage.id);
    }
  }, [data]);

  const [updateSite] = useMutation(UPDATE_SITE);

  const onPosterImageFile = useCallback(async (file: File) => {
    setLogoImageFile(file);

    const data = new FormData();
    data.append('image', file);

    const result = await axios.post('/api/v0/images', data);

    setLogoImageId(result.data.id);
    setLogoImageUrl(result.data.url);

    await updateSite({
      variables: { input: { legacyLogoImageId: result.data.id } },
    });
  }, []);

  const onOpenCodeEditorClick = useCallback(() => {
    const proxy = openIde({ value: '' })
      .on('preview', () => {})
      .on('SAVE_EDITOR_VALUE', async ({ value }: any) => {
        await updateSite({
          variables: { input: { headerHtml: value.value } },
        });
        await refetch();
      });

    proxy._messenger.on('READY', () => {
      if (data && data.site && data.site.headerHtml) {
        proxy._messenger.send('UPDATE_VALUE', data.site.headerHtml);
      }
    });
  }, [data]);

  const onOpenHeaderOverrideCodeEditorClick = useCallback(() => {
    const proxy = openIde({ value: '' })
      .on('preview', () => {})
      .on('SAVE_EDITOR_VALUE', async ({ value }: any) => {
        await updateSite({
          variables: {
            input: {
              headerOverrideHtml:
                value.value.trim().length > 0 ? value.value : null,
            },
          },
        });
        await refetch();
      });

    proxy._messenger.on('READY', () => {
      if (data && data.site && data.site.headerOverrideHtml) {
        proxy._messenger.send('UPDATE_VALUE', data.site.headerOverrideHtml);
      }
    });
  }, [data]);

  const onOpenFooterOverrideCodeEditorClick = useCallback(() => {
    const proxy = openIde({ value: '' })
      .on('preview', () => {})
      .on('SAVE_EDITOR_VALUE', async ({ value }: any) => {
        await updateSite({
          variables: {
            input: {
              footerOverrideHtml:
                value.value.trim().length > 0 ? value.value : null,
            },
          },
        });
        await refetch();
      });

    proxy._messenger.on('READY', () => {
      if (data && data.site && data.site.footerOverrideHtml) {
        proxy._messenger.send('UPDATE_VALUE', data.site.footerOverrideHtml);
      }
    });
  }, [data]);

  return {
    logoImageFile,
    logoImageId,
    logoImageUrl,
    onPosterImageFile,
    onOpenCodeEditorClick,
    onOpenHeaderOverrideCodeEditorClick,
    onOpenFooterOverrideCodeEditorClick,
  };
};
